import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, CircleHelp, LogOut, PenLine, ScrollText, Shield, UserRound } from 'lucide-react';
import PwaInstallButton from '../components/PwaInstallButton';
import { loadAuthenticatedBookingProfile } from '../lib/bookingService';
import { formatPhilippineMobileDisplay } from '../lib/phoneNumber';
import { supabase } from '../lib/supabaseClient';

const menuItems = [
  { id: 'edit', label: 'Edit Profile', icon: PenLine },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'terms', label: 'Terms & Policy', icon: ScrollText },
  { id: 'help', label: 'Help & Support', icon: CircleHelp },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await loadAuthenticatedBookingProfile();

        if (!profile.email && !localStorage.getItem('userAuthenticated')) {
          navigate('/login', { replace: true });
          return;
        }

        setUserEmail(profile.email);
        setUserName(profile.name);
        setUserPhone(profile.phone || '');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('userAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    navigate('/', { replace: true });
  };

  const handleMenuClick = (itemId) => {
    if (itemId === 'edit') {
      navigate('/profile/edit');
      return;
    }

    if (itemId === 'security') {
      navigate('/profile/security');
      return;
    }

    if (itemId === 'terms') {
      navigate('/profile/terms');
      return;
    }

    if (itemId === 'help') {
      navigate('/profile/help');
      return;
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100dvh-8rem)] overflow-hidden bg-[#141414] text-white grid place-items-center px-6 py-6">
        <p className="text-sm text-white/60">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100dvh-8rem)] bg-[#141414] text-white px-5 py-4 md:py-6">
      <div className="mx-auto flex max-w-sm flex-col items-center justify-start pt-3 md:pt-4">
        <div className="flex flex-col items-center text-center">
          <div className="grid h-24 w-24 place-items-center rounded-full border-2 border-white bg-transparent">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-white text-[#141414]">
              <UserRound size={34} strokeWidth={1.75} />
            </div>
          </div>

          <h1 className="mt-1 text-[26px] font-semibold leading-tight tracking-tight text-white">
            {userName || 'Your Profile'}
          </h1>
          <p className="mt-2 text-[13px] text-white/55">{userEmail || 'No email available'}</p>
          <p className="mt-1 text-[13px] text-white/55">
            {userPhone ? formatPhilippineMobileDisplay(userPhone) : 'No mobile number saved'}
          </p>
        </div>

        <div className="mt-4 w-full space-y-3">
          {menuItems.map((item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleMenuClick(item.id)}
                className="flex w-full items-center justify-between rounded-[22px] bg-[#232323] px-4 py-4 text-left shadow-[0_12px_24px_rgba(0,0,0,0.28)] transition active:scale-[0.99]"
              >
                <span className="flex items-center gap-3">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-white/5 text-white/90">
                    <Icon size={16} />
                  </span>
                  <span className="text-[14px] font-medium text-white/90">{item.label}</span>
                </span>
                <ChevronRight size={16} className="text-white/75" />
              </button>
            );
          })}

          <PwaInstallButton variant="profile" label="Install App" />

          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center justify-between rounded-[22px] bg-[#232323] px-4 py-4 text-left shadow-[0_12px_24px_rgba(0,0,0,0.28)] transition active:scale-[0.99]"
          >
            <span className="flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-white/5 text-white/90">
                <LogOut size={16} />
              </span>
              <span className="text-[14px] font-medium text-white/90">Log Out</span>
            </span>
            <ChevronRight size={16} className="text-white/75" />
          </button>
        </div>
      </div>
    </div>
  );
}